/**
 * Gallery scene: List overflow (?test=maater-ui#list-overflow)
 *
 * Same family as Browse focus "Show N hidden" (Eye + italic muted).
 * Import: ShowOverflowLink + @/lib/list-overflow
 */
import { useMemo, useState } from 'react'
import { Eye } from 'lucide-react'
import { ShowOverflowLink } from '@/components/ui/show-overflow-link'
import {
  LIST_OVERFLOW_PRIMARY_CAP,
  formatShowMoreWorkspacesLabel,
  partitionListOverflow,
} from '@/lib/list-overflow'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

const DEMO_WORKSPACES = [
  'Hop Studios',
  'Greater Good',
  'Personal',
  'Maater',
  'Client Alpha',
  'Client Bravo',
  'Archive lab',
  'Sandbox',
]

const DEMO_BROWSE_FILES = [
  '2026-05-01-kickoff.md',
  '2026-05-02-notes.md',
  '2026-05-03-spec.md',
  '2026-05-04-review.md',
  '2026-05-05-qa.md',
  '2026-05-06-ship.md',
  '2026-05-07-retro.md',
  '2026-05-08-follow-up.md',
]

export function ListOverflowScene() {
  const [wsExpanded, setWsExpanded] = useState(false)
  const [browseExpanded, setBrowseExpanded] = useState(false)

  const wsPartition = useMemo(
    () => partitionListOverflow(DEMO_WORKSPACES, { expanded: wsExpanded }),
    [wsExpanded],
  )
  const browsePartition = useMemo(
    () => partitionListOverflow(DEMO_BROWSE_FILES, { expanded: browseExpanded, cap: 4 }),
    [browseExpanded],
  )

  return (
    <div className="space-y-8" data-testid={testIds.maaterUiGallery.listOverflow}>
      <p className="text-sm text-muted-foreground">
        <strong className="font-medium text-foreground">List overflow</strong> - primary rows by
        activity (cap {LIST_OVERFLOW_PRIMARY_CAP}), then Eye + italic{' '}
        <code className="text-xs">Show N more</code>. Workspace pickers pin{' '}
        <strong className="font-medium text-foreground">active first</strong> (MRU among active),
        then inactive. Same category as Browse focus{' '}
        <code className="text-xs">.browse-show-hidden-link</code>. Import{' '}
        <code className="text-xs">ShowOverflowLink</code> - do not invent a second show-more
        control.
      </p>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Workspace picker (MRU + Show N more)
        </h3>
        <div className="max-w-sm rounded-md border border-border bg-popover p-2 shadow-sm">
          <p className="mb-1.5 px-1.5 text-sm font-semibold">Active workspaces</p>
          <div className="flex flex-col gap-1">
            {wsPartition.primary.map((name) => (
              <div
                key={name}
                className={cn(
                  'rounded-sm border border-border/60 px-2 py-1.5 text-sm',
                  'bg-background',
                )}
              >
                {name}
              </div>
            ))}
            <ShowOverflowLink
              expanded={wsExpanded}
              overflowCount={wsPartition.overflowCount}
              onToggle={() => setWsExpanded((v) => !v)}
              showMoreLabel={formatShowMoreWorkspacesLabel(wsPartition.overflowCount)}
              data-testid={testIds.maaterUiGallery.listOverflowShowMore}
            />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Browse family (Show N hidden)
        </h3>
        <div className="max-w-sm rounded-md border border-border bg-sidebar p-2">
          <p className="mb-1 px-2 text-[11px] font-medium text-muted-foreground">
            2026-05-06-to-202…
          </p>
          <div className="flex flex-col">
            {browsePartition.primary.map((name) => (
              <div
                key={name}
                className="truncate px-2 py-1 text-xs text-sidebar-foreground"
              >
                {name}
              </div>
            ))}
            <button
              type="button"
              className="browse-show-hidden-link"
              onClick={() => setBrowseExpanded((v) => !v)}
            >
              <Eye className="h-3 w-3 shrink-0" aria-hidden />
              <span>
                {browseExpanded
                  ? 'Show less'
                  : `Show ${browsePartition.overflowCount} hidden files`}
              </span>
            </button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Production Browse keeps the SCSS class; pickers and menus use{' '}
          <code className="text-xs">ShowOverflowLink</code> with the same Eye + italic language.
        </p>
      </section>
    </div>
  )
}
